import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../axiosConfig";
import usePermiso from "../../hooks/usePermiso";

function pad(n) {
  return String(n).padStart(2, "0");
}

function fechaISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtDiaLaboral(fecha) {
  const d = new Date(`${fecha}T12:00:00`);
  return d.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getISOWeekNumber(fechaStr) {
  const [y, m, d] = fechaStr.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

function siguienteDiaLaborableHoy() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  const dow = d.getDay();
  if (dow === 0) d.setDate(d.getDate() + 1);
  else if (dow === 6) d.setDate(d.getDate() + 2);
  return fechaISO(d);
}

function filaScrollObjetivo(rows) {
  if (!rows?.length) return null;
  const hoy = siguienteDiaLaborableHoy();
  if (rows.some((r) => r.fecha === hoy)) return hoy;
  const futuro = rows.find((r) => r.fecha >= hoy);
  if (futuro) return futuro.fecha;
  return rows[rows.length - 1].fecha;
}

export default function ModalGuardiaDia({ isOpen, onClose, theme, onGuardiasChanged }) {
  const { puede } = usePermiso();
  const puedeModificarAsignar = puede("ag_guardia_dia_asignar", "modificar");
  const puedeVerReset = puede("ag_guardia_dia_reset", "ver");
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ anchor_fecha: "", rotacion: [], agentes: [] });
  const [loading, setLoading] = useState(false);
  const [savingFecha, setSavingFecha] = useState(null);
  const [error, setError] = useState("");
  const filaActualRef = useRef(null);

  const inp = {
    width: "100%",
    background: isDark ? c.bgInput : "#ffffff",
    border: `1px solid ${isDark ? c.border : "#d0d0c8"}`,
    borderRadius: 7,
    padding: "5px 8px",
    color: c.textPrimary,
    fontSize: 12,
    boxSizing: "border-box",
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [mRes, lRes] = await Promise.all([
        api.get("/guardias-dia/meta"),
        api.get("/guardias-dia", { params: { year } }),
      ]);
      setMeta(mRes.data?.data || { anchor_fecha: "", rotacion: [], agentes: [] });
      setRows(lRes.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error cargando guardias día");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    if (!isOpen) return;
    cargar();
  }, [isOpen, cargar]);

  const filaObjetivo = filaScrollObjetivo(rows);

  useEffect(() => {
    if (!isOpen || loading || rows.length === 0) return;
    const id = requestAnimationFrame(() => {
      filaActualRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, loading, rows, year]);

  const guardarFila = async (row, agenteId) => {
    setSavingFecha(row.fecha);
    setError("");
    try {
      const res = await api.put(`/guardias-dia/${row.fecha}`, { agente_id: agenteId });
      setRows(res.data?.data || []);
      onGuardiasChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error guardando");
    } finally {
      setSavingFecha(null);
    }
  };

  const onAgentChange = (row, agentId) => {
    if (!agentId) return;
    guardarFila(row, agentId);
  };

  const regenerarAno = async () => {
    if (
      !window.confirm(
        `¿Quitar todas las asignaciones manuales de guardia día en ${year} y volver al cálculo automático (vacaciones y rotación)?`
      )
    ) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/guardias-dia/regenerar", {}, { params: { year } });
      setRows(res.data?.data || []);
      onGuardiasChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error al regenerar");
    } finally {
      setLoading(false);
    }
  };

  const textoCeldaTabla = "#111827";
  const textoEditado = "#b45309";

  const getRowBg = (idx, esHoy) => {
    if (esHoy) return isDark ? "#16a34a" : "#4ade80";
    return idx % 2 === 0
      ? (isDark ? "#fdba74" : "#ffedd5")
      : (isDark ? "#7dd3fc" : "#dbeafe");
  };

  const headBg = isDark ? c.bgInput : "#e8e8e0";
  const headSep = isDark ? c.border : "#c8c8be";
  const thHead = {
    padding: "8px 6px",
    fontWeight: 600,
    background: headBg,
    position: "sticky",
    top: 0,
    zIndex: 2,
    borderBottom: `1px solid ${headSep}`,
  };

  if (!isOpen) return null;

  const agentOpts = meta.agentes || [];
  const yearOptions = [year - 1, year, year + 1];
  const hoyLab = siguienteDiaLaborableHoy();
  const manualesEnAno = rows.filter((r) => r.editado_manual).length;
  const diasDesplazadosPorManual = rows.filter((r) => r.desplazada_por_manual_previo).length;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#00000088",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: isDark ? c.bgPanel : "#fafaf6",
          borderRadius: 12,
          width: "min(920px, 96vw)",
          maxHeight: "min(90vh, 820px)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          border: `1px solid ${isDark ? c.border : "#d8d8d0"}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px",
            borderBottom: `1px solid ${isDark ? c.border : "#d8d8d0"}`,
          }}
        >
          <div>
            <span style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary }}>📌 Día de guardia</span>
            <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
              Lun–vie (sin festivos). Rotación: Sabrina → Josefa → Rita → Antonia · ancla {meta.anchor_fecha || "2026-05-15"}
              {meta.agentes?.length > 0 && (
                <> · pool: {meta.agentes.map((a) => a.nombre).join(", ")}</>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", color: c.textMuted, fontSize: 22, cursor: "pointer" }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, color: c.textSecondary }}>Año</label>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} style={{ ...inp, width: 100 }}>
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {puedeVerReset && (
            <button
              type="button"
              onClick={regenerarAno}
              disabled={loading}
              style={{
                background: isDark ? "#334155" : "#e2e8f0",
                border: `1px solid ${isDark ? c.border : "#94a3b8"}`,
                color: c.textPrimary,
                borderRadius: 8,
                padding: "6px 12px",
                cursor: loading ? "wait" : "pointer",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Restablecer año (quitar manuales)
            </button>
          )}
          {loading && <span style={{ fontSize: 12, color: c.textMuted }}>Cargando…</span>}
          {savingFecha && (
            <span style={{ fontSize: 12, color: isDark ? "#fcd34d" : "#b45309" }}>
              Guardando {fmtDiaLaboral(savingFecha)}…
            </span>
          )}
        </div>

        {error && (
          <div style={{ margin: "0 18px 8px", padding: "8px 12px", borderRadius: 8, background: "#fef2f2", color: "#b91c1c", fontSize: 12 }}>
            {error}
          </div>
        )}

        {!loading && manualesEnAno > 0 && (
          <div
            style={{
              margin: "0 18px 8px",
              padding: "10px 12px",
              borderRadius: 8,
              background: isDark ? "#422006" : "#fffbeb",
              border: `1px solid ${isDark ? "#a16207" : "#f59e0b"}`,
              color: isDark ? "#fde68a" : "#92400e",
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            Hay <strong>{manualesEnAno}</strong> día{manualesEnAno === 1 ? "" : "s"} con guardia <strong>manual</strong> en {year}. Eso desplaza la cadena en otros días: <strong>Guardia</strong> muestra la cadena tras manuales; <strong>Cálculo auto</strong> es la rotación pura (vacaciones + reglas).
            {diasDesplazadosPorManual > 0 && (
              <> Hay <strong>{diasDesplazadosPorManual}</strong> fila{diasDesplazadosPorManual === 1 ? "" : "s"} sin manual marcado pero con asignación desplazada (etiqueta «desplazada»).</>
            )}{" "}
            Para ver solo el cálculo automático en todo el año, usa <strong>Restablecer año</strong>.
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto", padding: "0 18px 12px", minHeight: 0 }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
            <thead>
              <tr style={{ color: c.textMuted, textAlign: "left" }}>
                <th style={{ ...thHead, width: 44 }} title="Semana ISO">Sem.</th>
                <th style={thHead}>Día laborable</th>
                <th style={{ ...thHead, minWidth: 200 }}>Guardia</th>
                <th style={thHead}>Cálculo auto</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isSaving = savingFecha === row.fecha;
                const esHoy = row.fecha === hoyLab;
                const esScroll = row.fecha === filaObjetivo;
                const semanaAno = row.semana ?? getISOWeekNumber(row.fecha);
                return (
                  <tr
                    key={row.fecha}
                    ref={esScroll ? filaActualRef : null}
                    style={{
                      borderTop: `1px solid ${isDark ? c.border : "#e8e8e0"}`,
                      opacity: isSaving ? 0.6 : 1,
                      background: getRowBg(idx, esHoy),
                      boxShadow: esHoy ? (isDark ? "inset 0 0 0 2px #14532d" : "inset 0 0 0 2px #15803d") : "none",
                    }}
                  >
                    <td style={{ padding: "8px 6px", color: textoCeldaTabla, fontWeight: 600, textAlign: "center" }}>
                      {semanaAno}
                    </td>
                    <td style={{ padding: "8px 6px", whiteSpace: "nowrap", color: textoCeldaTabla }}>
                      {fmtDiaLaboral(row.fecha)}
                      {row.editado_manual && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: textoEditado, fontWeight: 700 }}>manual</span>
                      )}
                      {row.desplazada_por_manual_previo && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: isDark ? "#fbbf24" : "#b45309", fontWeight: 600 }} title="Este día no está marcado manual, pero la asignación se desplazó por un manual anterior en el año. Mira Cálculo auto o restablece el año.">
                          desplazada
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "6px", minWidth: 200 }}>
                      <select
                        value={row.agente_id || ""}
                        onChange={(e) => onAgentChange(row, e.target.value)}
                        disabled={isSaving || !puedeModificarAsignar}
                        title={!puedeModificarAsignar ? "No tienes permiso para modificar la guardia" : undefined}
                        style={{ ...inp, cursor: puedeModificarAsignar ? "pointer" : "not-allowed" }}
                      >
                        <option value="">—</option>
                        {agentOpts.map((a) => (
                          <option key={a.id} value={a.id}>{a.nombre}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: "8px 6px", color: textoCeldaTabla, fontSize: 11 }}>
                      {row.computed_nombre || "—"}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: 24, textAlign: "center", color: c.textMuted }}>
                    No hay días laborables en este año
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            padding: "12px 18px",
            borderTop: `1px solid ${isDark ? c.border : "#d8d8d0"}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 11, color: c.textMuted, maxWidth: 520 }}>
            Los festivos activos cierran el día: no hay guardia y la rotación sigue en el siguiente laborable.
            Las vacaciones/baja (todo el día): quien vuelve el **primer laborable** tras su ausencia hace guardia ese día (orden Sabrina → Josefa → Rita → Antonia si varias vuelven el mismo día). Si ese día es **lunes** tras fin de semana, el **martes** sigue la secuencia que quedó el **viernes** entre las que no estaban de vacaciones ese viernes. El resto encadena después de quien guardó el laborable anterior; el **primer** encadenado tras ese martes omite en el recorrido a quien **reincorporó el lunes** y a quien **guardó el martes** por la cola del viernes (evita dobles si hay festivo entre martes y jueves).
            Si cambias un día a mano, la asignación automática de ese día pasa al siguiente laborable y el resto se desplaza hasta el siguiente manual o fin de año. Si <strong>Guardia</strong> y <strong>Cálculo auto</strong> no coinciden y no ves «manual» en la fila, revisa el aviso amarillo arriba o restablece el año.
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: `1px solid ${isDark ? c.border : "#c8c8be"}`,
              color: c.textMuted,
              borderRadius: 8,
              padding: "7px 14px",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
